/**
 * Tank Context for ReefDataHub
 *
 * Provides tank selection state and methods throughout the application.
 * The tanks LIST lives in React Query (useTanks — cached and persisted);
 * this context owns only the selection, persisted to localStorage.
 */
import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Tank, CreateTankData, TankContextType } from '../types/tank';
import { buildApiUrl } from '../api/config';
import { apiClient } from '../api/client';
import { tanksQueryKey } from '../api/tanks';
import { useTanks } from '../hooks/useTanks';
import { useAuth } from './AuthContext';

const TankContext = createContext<TankContextType | undefined>(undefined);

const SELECTED_TANK_KEY = 'selectedTankId';

export const TankProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const { data: tanksData, isLoading, isSuccess, isError, error: tanksError, refetch } = useTanks();
  const tanks = useMemo(() => tanksData ?? [], [tanksData]);
  const [selectedTankId, setSelectedTankId] = useState<string | null>(
    () => localStorage.getItem(SELECTED_TANK_KEY)
  );

  // True once the tanks query has SETTLED for an authenticated user — success
  // OR error, so onboarding consumers (OnboardingGate, WelcomeWizard,
  // NoTankSelected, GettingStartedCard/useGettingStarted) never hang on a
  // failed fetch and never decide "this user has no tanks" from a first paint.
  //
  // Both terms independently reset it on logout: useTanks is disabled while
  // unauthenticated (so it can never settle), and logout clears the query
  // cache, which drops the settled status outright.
  const isInitialized = isAuthenticated && (isSuccess || isError);

  // Mirrors live auth state for async callbacks. selectTank awaits the network
  // before writing selection state; if the user logs out mid-flight that write
  // must not resurrect a selection the logout cleanup just wiped.
  //
  // useLayoutEffect, NOT useEffect: passive effects are flushed asynchronously
  // after the commit, so a logout render can commit and an in-flight
  // activation can resolve in the same tick, BEFORE the ref has been updated —
  // the stale `true` then lets the write through. Layout effects run
  // synchronously during commit, closing that window.
  //
  // Not separately unit-tested: the window cannot be reproduced under
  // jsdom/RTL, because every mechanism available for committing a render in a
  // test (act(), flushSync()) drains the passive effect queue before handing
  // control back, so useEffect and useLayoutEffect are indistinguishable there.
  // The mid-activation-logout test below covers the guard itself.
  const isAuthenticatedRef = useRef(isAuthenticated);
  useLayoutEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Resolve the selected tank against the current list; fall back to the
  // first tank when there's no valid saved selection.
  const selectedTank = useMemo(() => {
    if (tanks.length === 0) return null;
    return tanks.find((t) => t.id === selectedTankId) ?? tanks[0] ?? null;
  }, [tanks, selectedTankId]);

  // Keep localStorage in sync when the fallback changed the effective selection.
  // Skipped while logged out so the logout cleanup below can't be undone by
  // still-cached tanks data racing the cache clear.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (selectedTank && selectedTank.id !== selectedTankId) {
      setSelectedTankId(selectedTank.id);
      localStorage.setItem(SELECTED_TANK_KEY, selectedTank.id);
    }
  }, [isAuthenticated, selectedTank, selectedTankId]);

  // Clear selection state when the user logs out. Only wipe once auth has
  // RESOLVED to unauthenticated — on a warm reload isAuthenticated is false
  // until /auth/me completes, and clearing then would discard the saved
  // selection on every page load.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setSelectedTankId(null);
      localStorage.removeItem(SELECTED_TANK_KEY);
    }
  }, [authLoading, isAuthenticated]);

  const refreshTanks = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const selectTank = useCallback(async (tankId: string) => {
    const tank = tanks.find((t) => t.id === tankId);
    if (!tank) {
      console.warn(`Tank with id ${tankId} not found`);
      return;
    }

    let activationFailed = false;
    try {
      // Call activation endpoint to reissue JWT with new active_tank_id
      const response = await apiClient(buildApiUrl(`/api/tanks/${tankId}/activate`), {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Failed to activate tank: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to activate tank:', err);
      // Still update local selection even if activation fails (fallback),
      // but tell the user the server-side switch did not stick.
      activationFailed = true;
    }

    // The user may have logged out while activation was in flight; the logout
    // cleanup has already cleared the selection and must not be undone here.
    if (!isAuthenticatedRef.current) return;

    setSelectedTankId(tankId);
    localStorage.setItem(SELECTED_TANK_KEY, tankId);

    if (activationFailed) {
      notifications.show({
        id: 'tank-activation-failed',
        title: 'Tank switch not synced',
        message: `Switched to "${tank.name}" in this browser, but the server could not be updated. Some data may still reflect the previous tank.`,
        color: 'orange',
      });
    }
  }, [tanks]);

  const createTank = useCallback(async (data: CreateTankData): Promise<Tank> => {
    const response = await apiClient(buildApiUrl('/api/tanks'), {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const newTank: Tank = await response.json();
    queryClient.setQueryData<Tank[]>(tanksQueryKey, (prev) => [...(prev ?? []), newTank]);

    if (tanks.length === 0) {
      setSelectedTankId(newTank.id);
      localStorage.setItem(SELECTED_TANK_KEY, newTank.id);
    }

    return newTank;
  }, [queryClient, tanks.length]);

  const value = useMemo(
    () => ({
      tanks: isAuthenticated ? tanks : [],
      selectedTank: isAuthenticated ? selectedTank : null,
      isLoading,
      isInitialized,
      error: tanksError ? (tanksError as Error).message : null,
      selectTank,
      createTank,
      refreshTanks,
    }),
    [isAuthenticated, tanks, selectedTank, isLoading, isInitialized, tanksError, selectTank, createTank, refreshTanks]
  );

  return <TankContext.Provider value={value}>{children}</TankContext.Provider>;
};

export const useTank = (): TankContextType => {
  const context = useContext(TankContext);
  if (!context) {
    throw new Error('useTank must be used within TankProvider');
  }
  return context;
};
